import { memo, useState, useCallback, useEffect, useMemo, useRef, type CSSProperties } from 'react'
import { Archive, ArchiveRestore, Check, ChevronLeft, Copy, Grid2X2, Link2 } from 'lucide-react'
import { useActiveChannelId, useHasPermission, useStore, useProgramQuery, usePrograms, useProgramsUpdateSeq } from '../../store/index.js'
import { useChannelList } from '../../hooks/useChannels.js'
import { useProgramList, useProgramDetailById } from '../../hooks/usePrograms.js'
import { useCatalogOverview } from '../../hooks/useCatalogOverview.js'
import { useSeriesCatalog } from '../../hooks/useSeriesCatalog.js'
import type { Program } from '../../types/index.js'
import type { Collection, Series } from '../../types/domain.js'
import { ANTENNA_ELEMENT_PROGRAM_TYPES } from '../../lib/utils.js'
import type { ProbeResult } from '../../lib/mediaBrowserApi.js'
import { ProgramList }       from './ProgramList.js'
import { ProgramCatalog }    from './ProgramCatalog.js'
import { ProgramForm }       from './ProgramForm.js'
import type { ProgramFormHandle, ProgramFormStatus, ProgramPartsBridge } from './ProgramForm.js'
import { ProgramSidebar }    from './ProgramSidebar.js'
import { ProgramDiffusionsTab, ProgramHistoryTab, ProgramMediaTab, ProgramPartsTab } from './ProgramDetailTabs.js'
import { NewProgramModal }   from './NewProgramModal.js'
import { MediaBrowserModal } from './MediaBrowserModal.js'
import { MediaProgramImportModal } from './MediaProgramImportModal.js'
import { CollectionsPanel }  from './CollectionsPanel.js'
import { CollectionDetail }  from './CollectionDetail.js'
import { ProgramsOverview }  from './ProgramsOverview.js'
import { SeriesWorkbench }   from './SeriesWorkbench.js'
import { ProgramCategoriesPanel } from './ProgramCategoriesPanel.js'
import { ProgramGenresPanel } from './ProgramGenresPanel.js'
import { PageHeader, PageTabs, type PageTab } from '../common/PageChrome.js'
import { ResizeHandle } from '../common/ResizeHandle.js'
import './ProgramsView.css'

// ─── Poignée de redimensionnement ─────────────────────────────────────────────

// ─── Types ────────────────────────────────────────────────────────────────────

type CatalogView = 'list' | 'collections' | 'series' | 'genres' | 'categories'

const CATALOG_TABS: readonly PageTab[] = [
  { id: 'list', label: 'Programmes' },
  { id: 'collections', label: 'Collections' },
  { id: 'series', label: 'Séries' },
  // Le genre répond à « de quoi ça parle » et part à l'EPG ; le format à
  // « comment c'est produit ». Deux onglets, deux axes distincts.
  { id: 'genres', label: 'Genres' },
  { id: 'categories', label: 'Formats' },
]

const DETAIL_TABS = ['Vue d’ensemble', 'Parties', 'Diffusions', 'Médias', 'Historique'] as const

export interface ActiveCatalogFilter {
  type:  'collection' | 'series'
  id:    string
  name:  string
  color?: string | null
}

// ─── Composant racine ─────────────────────────────────────────────────────────

const MIN_W = 140
const MAX_W = 560
const COMPACT_W = 200

interface ProgramsViewProps {
  onAddToGrid?: (program: Program) => void
}

export const ProgramsView = memo(function ProgramsView({ onAddToGrid }: ProgramsViewProps = {}) {
  useChannelList()
  const activeChannelId = useActiveChannelId()
  const canManage = useHasPermission('PROGRAM_MANAGE')
  const canManageTaxonomy = useHasPermission('CATALOG_TAXONOMY_MANAGE')
  const canAddToGrid = useHasPermission('GRID_ITEM_CREATE')
  const setProgramQuery = useStore(state => state.setProgramQuery)
  const setSelectedProgram = useStore(state => state.setSelectedProgram)

  const [selectedId,   setSelectedId]   = useState<string | null>(null)
  const [showNewModal, setShowNewModal]  = useState(false)
  // Import d'un média : navigateur de fichiers, puis fiche pré-remplie à
  // confirmer. Rien n'est créé tant que l'opérateur n'a pas validé la fiche.
  const [showMediaBrowser, setShowMediaBrowser] = useState(false)
  const [importedMedia,    setImportedMedia]    = useState<ProbeResult | null>(null)
  const [leftWidth,    setLeftWidth]     = useState(264)
  const [rightWidth,   setRightWidth]    = useState(264)
  // Navigateur de parties + aperçu EPG publiés par la fiche vers le panneau droit.
  const [partsBridge, setPartsBridge] = useState<ProgramPartsBridge | null>(null)
  const programFormRef = useRef<ProgramFormHandle>(null)
  const [formStatus, setFormStatus] = useState<ProgramFormStatus | null>(null)
  const [detailTab, setDetailTab] = useState<(typeof DETAIL_TABS)[number]>('Vue d’ensemble')
  const [catalogView,  setCatalogView]   = useState<CatalogView>('list')
  const [activeFilter, setActiveFilter]  = useState<ActiveCatalogFilter | null>(null)
  const [selectedSeriesId, setSelectedSeriesId] = useState<string | null>(null)
  // La collection entière, pas son seul identifiant : la liste vit dans
  // CollectionsPanel et la vue n'a pas de catalogue de collections à relire.
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null)

  const { detail: program, reload: reloadDetail } = useProgramDetailById(selectedId)
  const { reload: reloadPrograms } = useProgramList(activeChannelId)

  // Agrégats du catalogue, pour l'écran sans sélection. Rechargés quand le
  // catalogue change (création, association de média, archivage).
  const programsUpdateSeq = useProgramsUpdateSeq()
  const overview = useCatalogOverview(activeChannelId, programsUpdateSeq)
  const { series: seriesCatalog, loading: seriesLoading, reload: reloadSeries } = useSeriesCatalog(
    catalogView === 'series' ? activeChannelId : null,
    programsUpdateSeq,
  )
  const allPrograms = usePrograms()
  const catalogPrograms = useMemo(
    () => allPrograms.filter(item => !ANTENNA_ELEMENT_PROGRAM_TYPES.includes(item.type)),
    [allPrograms],
  )


  const handleSelect  = useCallback((p: Program) => {
    setPartsBridge(null)
    setFormStatus(null)
    setDetailTab('Vue d’ensemble')
    setSelectedId(p.id)
  }, [])
  const handleProgramsDeleted = useCallback((ids: string[]) => {
    if (selectedId && ids.includes(selectedId)) {
      setSelectedId(null)
      setSelectedProgram(null)
    }
  }, [selectedId, setSelectedProgram])
  const handleSaved   = useCallback((_updated: Program) => {
    reloadDetail()
    reloadPrograms()
  }, [reloadDetail, reloadPrograms])
  const handleArchive = useCallback((_id: string) => setSelectedId(null), [])
  const handleCreated = useCallback((p: Program) => {
    setShowNewModal(false)
    setPartsBridge(null)
    setSelectedId(p.id)
    reloadPrograms()
  }, [reloadPrograms])

  // La fiche importée arrive avec son média déjà associé : on l'ouvre pour que
  // l'opérateur voie tout de suite ce qui a été créé.
  const handleMediaImported = useCallback((p: Program) => {
    setImportedMedia(null)
    setCatalogView('list')
    setPartsBridge(null)
    setSelectedId(p.id)
    reloadPrograms()
  }, [reloadPrograms])

  const handleMediaChanged = useCallback(async () => {
    await reloadDetail()
    await reloadPrograms()
  }, [reloadDetail, reloadPrograms])

  // Ouvrir une collection affiche sa fiche au centre — même geste que pour une
  // série : on veut voir le contenu et son ordre avant d'entrer dans une fiche.
  const handleSelectCollection = useCallback((col: Collection) => {
    setSelectedCollection(col)
    setSelectedId(null)
    setSelectedProgram(null)
  }, [setSelectedProgram])

  /** Depuis la fiche, basculer au catalogue filtré pour y ajouter des fiches. */
  const handleShowCollectionInCatalog = useCallback((col: Collection) => {
    setProgramQuery({ collectionId: col.id, seriesId: undefined, type: undefined, search: undefined })
    setActiveFilter({ type: 'collection', id: col.id, name: col.name, color: col.color })
    setCatalogView('list')
  }, [setProgramQuery])

  // Sélectionner une série ouvre sa fiche au centre — pas la fiche d'un
  // épisode : on veut d'abord voir l'état de la série avant d'y entrer.
  const handleSelectSeries = useCallback((s: Series) => {
    setSelectedSeriesId(s.id)
    setSelectedId(null)
    setSelectedProgram(null)
  }, [setSelectedProgram])

  const handleSeriesChanged = useCallback(() => {
    reloadSeries()
    reloadPrograms()
  }, [reloadSeries, reloadPrograms])

  const handleClearFilter = useCallback(() => {
    setProgramQuery({ collectionId: undefined, seriesId: undefined })
    setActiveFilter(null)
  }, [setProgramQuery])

  const handleBackToCatalog = useCallback(() => {
    setSelectedId(null)
    setSelectedProgram(null)
    setFormStatus(null)
    setPartsBridge(null)
  }, [setSelectedProgram])

  const handleCatalogViewChange = useCallback((id: string) => {
    setCatalogView(id as CatalogView)
    // Changer d'onglet remet le centre à l'état d'accueil de la section :
    // le catalogue pour « Programmes », toutes les séries pour « Séries ».
    setSelectedSeriesId(null)
    if (id === 'list' || id === 'series') {
      setSelectedId(null)
      setSelectedProgram(null)
    }
  }, [setSelectedProgram])

  // Si le filtre est effacé par un autre chemin (ex: bouton "Tous" dans ProgramList),
  // on retire aussi la bannière.
  const currentQuery = useProgramQuery()
  useEffect(() => {
    if (activeFilter && !currentQuery.collectionId && !currentQuery.seriesId) {
      setActiveFilter(null)
    }
  }, [currentQuery.collectionId, currentQuery.seriesId, activeFilter])

  const onLeftResize  = useCallback((delta: number) => {
    setLeftWidth(w => Math.min(MAX_W, Math.max(MIN_W, w + delta)))
  }, [])

  const onRightResize = useCallback((delta: number) => {
    setRightWidth(w => Math.min(MAX_W, Math.max(MIN_W, w - delta)))
  }, [])

  // Une fiche ouverte impose la navigation compacte. Les filtres complets
  // reviennent avec la vue globale après « Retour ».
  const catalogCompact = catalogView === 'list' && !!program
  const showProgramCatalog = catalogView === 'list'
  // L'onglet Séries occupe toute la largeur, comme le catalogue Programmes :
  // la maquette Pen y pose ses trois colonnes fixes et son inspecteur, que les
  // panneaux redimensionnables de l'écran d'édition contrediraient.
  const showSeriesWorkbench = catalogView === 'series' && !program && !!activeChannelId

  return (
    <div className="tp-page-shell programs-view">
      {!program && <PageHeader
        title="Gestion des programmes"
        actions={(
          <>
            <button
              type="button"
              className="tp-button"
              aria-label="Importer un média"
              onClick={() => setShowMediaBrowser(true)}
              disabled={!canManage}
              title={!canManage ? 'Permission de gestion des programmes requise' : 'Créer une fiche à partir d’un fichier média'}
            >
              Importer un média
            </button>
            <button
              type="button"
              className="tp-button tp-button--primary"
              aria-label="Nouveau programme"
              onClick={() => setShowNewModal(true)}
              disabled={!canManage}
              title={!canManage ? 'Permission de gestion des programmes requise' : undefined}
            >
              + Nouveau
            </button>
          </>
        )}
      />}
      {!program && <PageTabs
        items={CATALOG_TABS}
        activeId={catalogView}
        onChange={handleCatalogViewChange}
        ariaLabel="Sections du catalogue"
        controlsId="programs-catalog-panel"
        idPrefix="programs-tab"
      />}

      {/* L'onglet Programmes sans fiche ouverte occupe toute la largeur : le
          catalogue porte lui-même ses trois zones (facettes, tableau, aperçu).
          Dès qu'une fiche est ouverte, on retrouve la disposition d'édition
          catalogue / formulaire / informations. */}
      {program ? (
        <div className="program-detail-shell">
          <header className="program-detail-header">
            <div className="program-detail-header__identity">
              <button type="button" className="program-detail-back" onClick={handleBackToCatalog} aria-label="Retour aux programmes">
                <ChevronLeft size={16} />
              </button>
              <div>
                <div className="program-detail-breadcrumb">PROGRAMMES / {program.programCode || 'SANS IDENTIFIANT'}</div>
                <h1 className="program-detail-title">{program.title || 'Sans titre'}</h1>
              </div>
            </div>
            <button
              type="button"
              className="tp-button tp-button--primary program-detail-save"
              onClick={() => programFormRef.current?.save()}
              disabled={!formStatus?.canManage || formStatus.saving || !formStatus.dirty}
            >
              {formStatus?.saving ? 'Enregistrement…' : <><Check size={14} /> Enregistrer</>}
            </button>
          </header>

          <div className="program-detail-actions">
            <div className="program-detail-actions__group">
              <button type="button" className="program-detail-action" onClick={() => programFormRef.current?.duplicate()} disabled={!formStatus?.canManage}>
                <Copy size={13} /> Dupliquer
              </button>
              <button type="button" className="program-detail-action" onClick={() => programFormRef.current?.matchMedia()} disabled={!formStatus?.canManage || formStatus.matchingMedia || program.isLive}>
                <Link2 size={13} /> {formStatus?.matchingMedia ? 'Recherche…' : 'Faire correspondre les médias'}
              </button>
              <button type="button" className="program-detail-action" onClick={() => onAddToGrid?.(program)} disabled={!canAddToGrid}>
                <Grid2X2 size={13} /> + Grille
              </button>
              {/* Une fiche archivée n'a pas à être archivée deux fois : le même
                  emplacement propose son retour au catalogue. */}
              {formStatus?.active === false ? (
                <button type="button" className="program-detail-action" onClick={() => programFormRef.current?.restore()} disabled={!formStatus?.canDelete || formStatus.archiving}>
                  <ArchiveRestore size={13} /> {formStatus?.archiving ? 'Désarchivage…' : 'Désarchiver'}
                </button>
              ) : (
                <button type="button" className="program-detail-action program-detail-action--danger" onClick={() => programFormRef.current?.archive()} disabled={!formStatus?.canDelete || formStatus.archiving}>
                  <Archive size={13} /> {formStatus?.archiving ? 'Archivage…' : 'Archiver'}
                </button>
              )}
            </div>
            {formStatus?.dirty && <span className="program-detail-dirty">MODIFICATIONS NON ENREGISTRÉES</span>}
          </div>

          <nav className="program-detail-tabs" aria-label="Sections de la fiche programme">
            {DETAIL_TABS.map(tab => (
              <button
                key={tab}
                type="button"
                className={`program-detail-tab${detailTab === tab ? ' is-active' : ''}`}
                onClick={() => setDetailTab(tab)}
              >
                {tab}
              </button>
            ))}
          </nav>

          <div className={`program-detail-body${detailTab === 'Vue d’ensemble' || detailTab === 'Parties' ? '' : ' is-hidden'}${detailTab === 'Parties' ? ' is-parts' : ''}`}>
              {detailTab === 'Parties' && <ProgramPartsTab program={program} bridge={partsBridge} />}
              <main className="program-detail-main">
                <ProgramForm
                  ref={programFormRef}
                  program={program}
                  onSaved={handleSaved}
                  onArchive={handleArchive}
                  onMediaChanged={handleMediaChanged}
                  onPartsBridge={setPartsBridge}
                  onStatusChange={setFormStatus}
                  externalChrome
                  mediaInSidebar
                  displayMode={detailTab === 'Parties' ? 'parts' : 'overview'}
                />
              </main>
              {detailTab === 'Vue d’ensemble' && <aside className="program-detail-sidebar">
                <ProgramSidebar
                  program={program}
                  onAddToGrid={onAddToGrid}
                  partsBridge={partsBridge}
                  onMediaChanged={handleMediaChanged}
                  mediaDisabled={formStatus?.dirty}
                  mediaDisabledReason="Enregistrez les modifications avant de gérer les médias."
                  detailMode
                />
              </aside>}
          </div>
          {detailTab === 'Diffusions' && <ProgramDiffusionsTab program={program} onAddToGrid={canAddToGrid ? onAddToGrid : undefined} />}
          {detailTab === 'Médias' && (
            <ProgramMediaTab
              program={program}
              onChanged={handleMediaChanged}
              disabled={formStatus?.dirty}
              disabledReason="Enregistrez les modifications avant de gérer les médias."
            />
          )}
          {detailTab === 'Historique' && <ProgramHistoryTab program={program} />}
        </div>
      ) : showProgramCatalog ? (
        <div
          id="programs-catalog-panel"
          className="programs-catalog-full"
          role="tabpanel"
          aria-labelledby={`programs-tab-${catalogView}`}
        >
          <ProgramCatalog
            channelId={activeChannelId}
            onEditProgram={handleSelect}
            onProgramsDeleted={handleProgramsDeleted}
          />
        </div>
      ) : showSeriesWorkbench ? (
        <div
          id="programs-catalog-panel"
          className="programs-catalog-full"
          role="tabpanel"
          aria-labelledby={`programs-tab-${catalogView}`}
        >
          <SeriesWorkbench
            channelId={activeChannelId}
            series={seriesCatalog}
            loading={seriesLoading}
            selectedSeriesId={selectedSeriesId}
            onSelectSeries={handleSelectSeries}
            onSelectProgram={handleSelect}
            onChanged={handleSeriesChanged}
            canManage={canManageTaxonomy}
          />
        </div>
      ) : (
      <div
        id="programs-catalog-panel"
        className={`programs-workspace${program ? ' has-selection' : ''}`}
        role="tabpanel"
        aria-labelledby={`programs-tab-${catalogView}`}
      >

        {/* Colonne gauche — vue catalogue */}
        <section
          className={`programs-catalog-pane${catalogCompact ? ' is-compact' : ''}`}
          aria-label="Catalogue"
          style={{ '--programs-pane-width': `${catalogCompact ? COMPACT_W : leftWidth}px` } as CSSProperties}
        >
          {showProgramCatalog && (
            <ProgramList
              channelId={activeChannelId}
              onSelectProgram={handleSelect}
              onProgramsDeleted={handleProgramsDeleted}
              activeFilter={activeFilter}
              onClearFilter={handleClearFilter}
              compact={catalogCompact}
            />
          )}
          {catalogView === 'collections' && activeChannelId && (
            <CollectionsPanel
              channelId={activeChannelId}
              onSelectCollection={handleSelectCollection}
              canManage={canManageTaxonomy}
              selectedCollectionId={selectedCollection?.id ?? null}
              onCollectionChanged={col => setSelectedCollection(current => current?.id === col.id ? col : current)}
              onCollectionDeleted={id => setSelectedCollection(current => current?.id === id ? null : current)}
            />
          )}
          {catalogView === 'genres' && activeChannelId && (
            <ProgramGenresPanel channelId={activeChannelId} canManage={canManageTaxonomy} />
          )}

          {catalogView === 'categories' && activeChannelId && (
            <ProgramCategoriesPanel channelId={activeChannelId} canManage={canManageTaxonomy} />
          )}
          {!showProgramCatalog && !activeChannelId && (
            <div className="catalog-list">
              <div className="catalog-empty">Sélectionnez une chaîne pour afficher ce catalogue.</div>
            </div>
          )}
        </section>

        {!catalogCompact && <ResizeHandle label="Redimensionner le catalogue" onDelta={onLeftResize} />}

        {/* Colonne centrale — formulaire + médias */}
        <main className="programs-detail-pane">
          {program ? (
            <>
              <div className="programs-detail-mobile-bar">
                <button type="button" className="tp-button" onClick={handleBackToCatalog}>← Retour</button>
              </div>
              <div className="programs-detail-scroll">
                <ProgramForm
                  program={program}
                  onSaved={handleSaved}
                  onArchive={handleArchive}
                  onMediaChanged={handleMediaChanged}
                  onPartsBridge={setPartsBridge}
                />
              </div>
            </>
          ) : (
            /* Sans sélection, le centre affiche l'état du catalogue plutôt
               qu'une invite vide — demande explicite du commanditaire.
               Dans l'onglet Séries, c'est le catalogue de séries qui tient ce
               rôle : toutes les séries, puis la fiche de celle qu'on ouvre. */
            <div className="programs-detail-scroll">
              {catalogView === 'collections' && activeChannelId && selectedCollection ? (
                <CollectionDetail
                  collection={selectedCollection}
                  channelId={activeChannelId}
                  canManage={canManageTaxonomy}
                  onSelectProgram={handleSelect}
                  onBack={() => setSelectedCollection(null)}
                  onShowInCatalog={handleShowCollectionInCatalog}
                />
              ) : (
                <ProgramsOverview
                  overview={overview}
                  programs={catalogPrograms}
                  onSelect={setSelectedId}
                />
              )}
            </div>
          )}
        </main>

        <ResizeHandle label="Redimensionner le panneau d’informations" onDelta={onRightResize} />

        {/* Colonne droite — statistiques */}
        <aside
          className="programs-meta-pane"
          aria-label="Informations du programme"
          style={{ '--programs-pane-width': `${rightWidth}px` } as CSSProperties}
        >
          {program
            ? <ProgramSidebar program={program} onAddToGrid={canAddToGrid ? onAddToGrid : undefined} partsBridge={partsBridge} />
            : null
          }
        </aside>
      </div>
      )}

      {showNewModal && activeChannelId && canManage && (
        <NewProgramModal channelId={activeChannelId} onCreated={handleCreated} onClose={() => setShowNewModal(false)} />
      )}

      {showMediaBrowser && canManage && (
        <MediaBrowserModal
          confirmLabel="Créer la fiche"
          onSelect={media => { setShowMediaBrowser(false); setImportedMedia(media) }}
          onClose={() => setShowMediaBrowser(false)}
        />
      )}

      {importedMedia && activeChannelId && canManage && (
        <MediaProgramImportModal
          channelId={activeChannelId}
          media={importedMedia}
          onCreated={handleMediaImported}
          onClose={() => setImportedMedia(null)}
        />
      )}
    </div>
  )
})
